<template>
    <BaseModal>
        <div class="report" @click.stop>
            <p class="report_title">{{ $t('report.title') }}</p>

            <div class="report_comment comment">
                <Avatar :user="comment?.author" class="user_avatar" />
                <div class="comment_box">
                    <div class="comment_info">
                        <div class="comment_text_info">
                            <p class="comment_username">{{ comment?.author?.username }}</p>
                            <div class="comment_date">{{ commentDate }}</div>
                        </div>
                    </div>

                    <div class="comment_text">
                        {{ comment?.text }}
                    </div>
                </div>
            </div>

            <form class="report_form" @submit.prevent="sendReport">
                <MySelect v-model="optionSelected" :list="categoryComplain" valueKey="id" labelKey="name"
                    :class="{ error: reportError }" />

                <p v-if="reportError" class="report_error">{{ reportError }}</p>

                <button class="report_send" :disabled="!optionSelected" type="submit">
                    {{ $t('report.send') }}
                </button>
            </form>
        </div>
    </BaseModal>
</template>

<script>
import BaseModal from "@/UI/Modals/BaseModal.vue";
import MySelect from "@/UI/MySelect.vue";
import Avatar from "@/components/Avatar.vue";
import {
    formatDistanceToNow,
    differenceInMinutes
} from "date-fns";
import { ru, enUS } from "date-fns/locale";
import MyNotification from "@/UI/MyNotification.vue";
import { getCategoryComplain, addComplainAboutComment } from "@/api/axios.js";
import { useModalStore } from "@/stores/modalStore";
import { useNotificationStore } from "@/stores/notificationStore";

export default {
    name: "Report",
    components: { BaseModal, MySelect, Avatar, MyNotification },
    props: {
        comment: {
            type: Object
        },
    },
    data() {
        return {
            optionSelected: null,
            now: new Date(),
            categoryComplain: [],
            reportError: "",
            modal: useModalStore(),
            notification: useNotificationStore(),
        }
    },
    methods: {
        async sendReport() {
            const data = {
                category: this.optionSelected,
                text: this.comment.text,
            };

            try {
                await addComplainAboutComment(this.comment.id, data);

                this.notification.show(this.$t('notification.complain'));

                const modalItem = this.modal.modals.slice(-1);
                this.modal.closeModal(modalItem.id);
            } catch (e) {
                this.reportError = this.$t('report.error');
                console.log("Send complain error: ", e);
            }
        }
    },
    computed: {
        commentDate() {
            const dateStr = this.comment?.time_created;
            if (!dateStr) return "";

            const dateUTC = new Date(dateStr);
            const dateLocal = new Date(
                dateUTC.getTime() + dateUTC.getTimezoneOffset() * 60000
            );

            if (differenceInMinutes(this.now, dateLocal) < 1) {
                return this.$t('justNow');
            }

            return formatDistanceToNow(dateLocal, {
                locale: this.$i18n.locale === 'ru' ? ru : enUS,
                addSuffix: true,
            });
        },
    },
    async mounted() {
        try {
            const res = await getCategoryComplain();
            this.categoryComplain = res.data;
        } catch (err) {
            console.error("Error fetching complain categories:", err);
            this.categoryComplain = [];
        }
    }
}
</script>

<style>
.report_error {
    color: #c44;
    font-size: 12px;
    font-weight: 400;
}

.report {
    padding: 20px 25px;
    width: 380px;
    background: var(--bg-color);
    border-radius: 33px;
    margin: auto 0;
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.report_title {
    font-size: 24px;
}

.report_form {
    display: flex;
    flex-direction: column;
    gap: 15px;
    height: 100%;
}

.report_send {
    border-radius: 10px;
    padding: 8px 15px;
    width: fit-content;
    color: black;
    background: var(--main-color);
    cursor: pointer;
    transition: background 0.2s ease;
    font-weight: 600;
}

.report_send:hover {
    background: var(--main-color-hover);
}

.report_send:hover:disabled {
    background: var(--main-color);
}

.report_send:disabled {
    opacity: 0.5;
    cursor: default;
}

.report_comment {
    border-radius: 15px;
    padding: 14px;
    border: 2px solid var(--main-color);
}
</style>
